import type { UsageSeries } from "api";
import { findProductByTool } from "constants/products.constants";
import type { LoadStatus } from "hooks/useProfile";
import { formatShortDate } from "utils/date.utils";
import { formatCount } from "utils/number.utils";
import { chartBars } from "./dashboard.model";
import { DashboardCard } from "./DashboardCard";

type CallsCardProps = {
  usage: UsageSeries | null;
  status: LoadStatus;
};

/** How many days the chart covers, and the window the hook asks for. */
export const CALLS_WINDOW_DAYS = 14;

/**
 * Calls per day, and which tools they went to.
 *
 * The series is assembled by the back office from the gateway's running monthly
 * counter, so it only exists from the day snapshotting started — `tracked_since`
 * is what lets the card say that instead of implying a fortnight of silence.
 */
export const CallsCard = ({ usage, status }: CallsCardProps) => {
  const bars = chartBars(usage);
  const startedMidWindow =
    usage !== null && usage.tracked_since !== null && usage.tracked_since > usage.from ? usage.tracked_since : null;

  return (
    <DashboardCard
      title="API calls"
      sub={
        usage === null
          ? ""
          : `Last ${CALLS_WINDOW_DAYS} days · ${formatCount(usage.total)} total${
              startedMidWindow === null ? "" : ` · counted from ${formatShortDate(startedMidWindow)}`
            }`
      }
      status={status}
      isEmpty={usage !== null && usage.total === 0}
      empty={
        usage?.tracked_since === null
          ? "No calls counted yet — the daily series starts with your first request."
          : "No calls in the last two weeks."
      }
      failure="Could not load your call history. Please refresh the page."
    >
      <div className="dsh__spark">
        {bars.map((bar) => (
          <span
            key={bar.date}
            style={{ height: `${bar.height}%` }}
            title={`${formatShortDate(bar.date)}: ${formatCount(bar.used)} calls`}
          />
        ))}
      </div>

      <ul className="dsh__ep">
        {usage?.by_tool.map((tool) => (
          <li key={tool.service_tool}>
            {/* The catalogue name wins over the back office label, as it does
                everywhere else the customer sees a product. */}
            <span>{findProductByTool(tool.service_tool)?.name ?? tool.label}</span>
            <b>{formatCount(tool.used)}</b>
          </li>
        ))}
      </ul>
    </DashboardCard>
  );
};
